import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { AddIcon } from 'components/icons/AddIcon';
import { CrossIcon } from 'components/icons/CrossIcon';
import { ErrorIcon } from 'components/icons/ErrorIcon';
import { breakpointMediumHandset } from 'common/constants';
import { screenMediumHandset } from 'common/constants';
import { css, cx } from 'linaria';
import isURL from 'validator/lib/isURL';
import { Trans, useTranslation } from 'next-i18next';
import { KEYS } from 'common/constants';
import Mousetrap from 'mousetrap';
const addStyle = "a14hwmit";
const addIconStyle = "a7o7dd7";
const closeIconStyle = "c6uzcx";
const closeLabelStyle = "c1xd9hu9";
const CloseLabel = ({
  children
}) => <span className={closeLabelStyle}>{children}</span>;
const closeButtonStyle = "cj9zxq3";
const CloseButton = ({
  onClick
}) => {
  return <button className={closeButtonStyle} onClick={onClick} data-cy="add-cancel">
      <CrossIcon className={closeIconStyle} />
      <CloseLabel>
        <Trans i18nKey="nav:cancel">Cancel</Trans>
      </CloseLabel>
    </button>;
};
const addContainerStyle = "a2ytnol";

/**
 * Component to expose search functionality to users in places like GlobalNav.
 */
const GlobalNavAdd = ({
  onSubmit,
  onClose,
  onFocus,
  onBlur,
  value,
  placeholder,
  mobilePlaceholder
}) => {
  const {
    t
  } = useTranslation();
  const inputEl = useRef(null);
  const [addUrl, updateAddUrl] = useState(value);
  const [isMobile, updateIsMobile] = useState(false);
  const [inputError, updateInputError] = useState(false);
  const handleInputChange = e => {
    updateInputError(false);
    updateAddUrl(e?.target?.value);
  };
  const handleSubmit = e => {
    e.stopPropagation();
    e.preventDefault();
    const validUrl = isURL(addUrl, {
      protocols: ['http', 'https'],
      allow_underscores: true
    });
    if (!validUrl) return updateInputError(t('nav:please-enter-a-valid-url'));
    const protocolRegEx = new RegExp('^https?://');
    const prefix = !!protocolRegEx.test(addUrl) ? '' : 'https://';
    onSubmit(`${prefix}${addUrl}`);
    onClose();
  };
  const handleKeyUp = event => {
    if (event.keyCode === KEYS.ESCAPE) inputEl.current.blur();
  };
  useEffect(() => {
    Mousetrap.bind('esc', onClose);
    return () => Mousetrap.unbind('esc');
  }, [onClose]);
  useEffect(() => {
    updateIsMobile(window.innerWidth < screenMediumHandset);
  }, []);
  useEffect(() => {
    inputEl.current.focus();
  }, []);
  return <form className={addStyle} onSubmit={handleSubmit} autoComplete="off">
      <div className={addContainerStyle}>
        <AddIcon className={addIconStyle} />
        <input type="url" name="add-input" ref={inputEl} className={cx('add-input', !!addUrl && 'has-value')} aria-label={t('nav:add-item-to-pocket', 'Add Item to Pocket')} value={addUrl} onChange={handleInputChange} onFocus={onFocus} onBlur={onBlur} onKeyUp={handleKeyUp} placeholder={isMobile ? t(mobilePlaceholder) : t(placeholder)} data-cy="add-input" enterKeyHint="send" />
        {inputError ? <div className="error-message">
            <div>
              <ErrorIcon /> {inputError}
            </div>
          </div> : null}
      </div>
      <button className="add-button" onClick={handleSubmit} data-cy="add-submit">
        <Trans i18nKey="nav:add">Add</Trans>
      </button>
      <input type="submit" className="mobile-submit" />
      {onClose ? <CloseButton onClick={onClose} data-cy="add-close" /> : null}
    </form>;
};
GlobalNavAdd.propTypes = {
  /**
   * Callback function for processing a user's search term
   */
  onSubmit: PropTypes.func.isRequired,
  /**
   * Callback function to run when a user clicks the Close button. Close button
   * will only appear if this function is present
   */
  onClose: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /**
   * Callback function when to run when a focuses on the search input. Receives
   * the event as a parameter
   */
  onFocus: PropTypes.func,
  /**
   * Callback function when the search input loses focus. Receives the event as
   * a parameter
   */
  onBlur: PropTypes.func,
  /**
   * Current value of the text input
   */
  value: PropTypes.string,
  /**
   * Text to display as a note to the user when the user input is still empty
   */
  placeholder: PropTypes.string,
  /**
   * Text to display as a note to the user when the user input is still empty,
   * at mobile widths
   */
  mobilePlaceholder: PropTypes.string
};
GlobalNavAdd.defaultProps = {
  onClose: false,
  onFocus: () => {},
  onBlur: () => {},
  value: '',
  placeholder: 'nav:save-a-url-https',
  mobilePlaceholder: 'nav:save-a-url'
};

// This is so the localization parser can pick up these runtime variables
// t('nav:save-a-url-https', 'Save a URL https://...')
// t('nav:save-a-url', 'Save a URL')

export default GlobalNavAdd;

require("./global-nav-add.linaria.module.css!=!../../../../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./global-nav-add.js");